import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { getDashboardSettings, submitLead } from "@/lib/api";
import OdooBooking from "@/components/OdooBooking";
import {
  Calendar, Clock, Building2, CheckCircle, Users, ArrowRight,
  Sparkles, Globe, Headphones
} from "lucide-react";

export default function ScheduleDemoPage() {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message || "Demo Request via Schedule Demo page",
        source: "schedule_demo_page"
      });
      setSubmitted(true);
      toast.success("Your demo request has been submitted!");
    } catch {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="schedule-demo-page">
      <SEOHead
        title="Schedule a Free ERP Demo"
        description="Book a free personalized demo of Odoo ERP, Focus X ERP, or custom solutions. See how Absolin can transform your business operations in just 30 minutes."
        keywords="schedule ERP demo, free ERP demo India, Odoo ERP demo, Focus X ERP demo, book consultation ERP, MSME ERP demo"
        canonicalUrl="/schedule-demo"
      />
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0066CC] to-[#004499] text-white pt-24 md:pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs font-medium mb-6">
            <Calendar className="w-3.5 h-3.5" /> Book a Free Demo
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            See How ABSOLIN Can Transform Your Business
          </h1>
          <p className="text-blue-100 text-sm max-w-2xl mx-auto leading-relaxed">
            Get a personalized walkthrough of our ERP solutions tailored to your industry.
            Our experts will show you exactly how we can help digitize your operations.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-8 pb-16">
        <div className="grid lg:grid-cols-5 gap-8">

          {/* Left: Lead Capture Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-0" data-testid="demo-lead-form">
              <CardContent className="p-6">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="font-semibold text-lg mb-1">Request Your Demo</h3>
                    <p className="text-xs text-slate-500 mb-4">Fill in your details and our team will reach out within 24 hours.</p>

                    <div className="space-y-1.5">
                      <Label htmlFor="demo-name" className="text-xs">Full Name *</Label>
                      <Input id="demo-name" placeholder="Your name" value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        data-testid="demo-name-input" className="placeholder:text-slate-300" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="demo-email" className="text-xs">Work Email *</Label>
                      <Input id="demo-email" type="email" placeholder="you@company.com" value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="demo-email-input" className="placeholder:text-slate-300" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="demo-phone" className="text-xs">Phone</Label>
                      <Input id="demo-phone" type="tel" placeholder="+91 98765 43210" value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        data-testid="demo-phone-input" className="placeholder:text-slate-300" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="demo-company" className="text-xs">Company Name</Label>
                      <Input id="demo-company" placeholder="Your company" value={formData.company}
                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                        data-testid="demo-company-input" className="placeholder:text-slate-300" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="demo-message" className="text-xs">What are you looking for?</Label>
                      <textarea id="demo-message" placeholder="Tell us about your requirements..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-ring min-h-[70px]"
                        data-testid="demo-message-input" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#0066CC] hover:bg-[#0055aa]" data-testid="demo-submit-btn">
                      {submitting ? "Submitting..." : "Request Demo"} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-8" data-testid="demo-success">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">Demo Request Received!</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Our team will contact you within 24 hours to schedule your personalized demo.
                    </p>
                    <OdooBooking
                      buttonText="Or Book a Time Slot Now"
                      buttonClassName="bg-[#0066CC] hover:bg-[#0055aa]"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Info + Calendly */}
          <div className="lg:col-span-3 space-y-6">
            {/* Value Props */}
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Clock, title: "30-Minute Session", desc: "Focused walkthrough tailored to your industry needs" },
                { icon: Building2, title: "Industry-Specific", desc: "Solutions customized for 21+ industry verticals" },
                { icon: Sparkles, title: "Live Product Demo", desc: "See the actual ERP system in action with real data" },
                { icon: Globe, title: "Implementation Plan", desc: "Get a roadmap with timelines and ROI projections" },
              ].map((item, i) => (
                <Card key={i} className="border-slate-200">
                  <CardContent className="p-4 flex gap-3">
                    <div className="bg-blue-50 rounded-lg p-2 h-fit">
                      <item.icon className="w-4 h-4 text-[#0066CC]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-0.5">{item.title}</h4>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Booking Widget or Contact Fallback */}
            <Card className="border-slate-200" data-testid="booking-section">
              <CardContent className="p-6 text-center space-y-4">
                <Calendar className="w-10 h-10 text-[#0066CC] mx-auto" />
                <h3 className="font-semibold text-lg">Book Your Demo Session</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  Select a convenient time to speak with our team about ERP solutions for your business.
                </p>
                <OdooBooking
                  verticalName="ERP Solutions"
                  buttonText="Pick a Date & Time"
                  buttonClassName="bg-[#0066CC] hover:bg-[#0055aa] mx-auto"
                />
                <div className="pt-3 border-t">
                  <p className="text-xs text-slate-400">
                    Or call us at <a href="tel:+918297555678" className="text-[#0066CC] font-semibold">+91 829 755 5678</a>
                    {" / "}email <a href="mailto:info@absolinsoft.com" className="text-[#0066CC] font-semibold">info@absolinsoft.com</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
